import { RefObject, useEffect, useRef } from 'react'
import { getTabbableElements } from '@/utils/focus'

interface FocusScopeOptions {
	/** Wrap Tab / Shift+Tab so focus cannot leave the container. */
	trap?: boolean
	/** Element to focus on activation. Falls back to the first tabbable element, then the container. */
	initialFocus?: () => HTMLElement | null
	/** Element to refocus on deactivation. Defaults to whatever had focus on activation; return null to skip. */
	returnFocus?: () => HTMLElement | null
}

/**
 * Moves focus into a container while `active`, optionally traps Tab inside it,
 * and returns focus when it deactivates.
 */
export function useFocusScope (
	containerRef: RefObject<HTMLElement | null>,
	active: boolean,
	options: FocusScopeOptions = {}
) {
	const optionsRef = useRef(options)

	useEffect(() => {
		optionsRef.current = options
	})

	useEffect(() => {
		if (!active) return

		const previous = document.activeElement as HTMLElement | null

		const handleKeyDown = (e: KeyboardEvent) => {
			const container = containerRef.current
			if (e.key !== 'Tab' || !optionsRef.current.trap || !container) return

			const tabbable = getTabbableElements(container)
			if (tabbable.length === 0) {
				e.preventDefault()
				container.focus()
				return
			}

			const first = tabbable[0]
			const last = tabbable[tabbable.length - 1]
			const current = document.activeElement

			// The container itself sits before `first` in tab order when it holds focus.
			if (e.shiftKey && (current === first || current === container)) {
				e.preventDefault()
				last.focus()
			} else if (!e.shiftKey && current === last) {
				e.preventDefault()
				first.focus()
			}
		}

		document.addEventListener('keydown', handleKeyDown)

		// Wait a frame so portalled content is mounted and positioned before focusing.
		const frame = requestAnimationFrame(() => {
			const container = containerRef.current
			if (!container) return
			const target =
				optionsRef.current.initialFocus?.() ??
				getTabbableElements(container)[0] ??
				container
			target.focus()
		})

		return () => {
			cancelAnimationFrame(frame)
			document.removeEventListener('keydown', handleKeyDown)
			const { returnFocus } = optionsRef.current
			const target = returnFocus ? returnFocus() : previous
			// `preventScroll` keeps the reader's scroll position instead of jumping back to the trigger.
			target?.focus({ preventScroll: true })
		}
	}, [active, containerRef])
}
